import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useTranslation } from '../hooks/useTranslation'
import { authFetchJson } from '../lib/authFetch'

// Matches the actual GET /api/v2/people/:id response (returned flat, NOT wrapped)
interface PersonAffiliation {
  id: number
  organization_id: number
  org_name: string
  role: string | null
  title: string | null
  start_date: string | null
  end_date: string | null
  is_primary: boolean | number | null
}

interface PersonProject {
  id: number
  title: string
  person_role: string
}

interface PersonDetail {
  id: number
  name: string
  email: string | null
  title: string | null
  bio: string | null
  linkedin_url: string | null
  orcid: string | null
  phone: string | null
  scholar_url: string | null
  h_index: number | null
  h_index_source: string | null
  area_of_expertise: string | null
  tags: string[] | null
  notes: string | null
  created_at: string | null
  updated_at: string | null
  affiliations: PersonAffiliation[]
  projects: PersonProject[]
}

type EditField =
  | 'name'
  | 'email'
  | 'title'
  | 'bio'
  | 'linkedin_url'
  | 'orcid'
  | 'phone'
  | 'scholar_url'
  | 'area_of_expertise'
  | 'notes'
  | null

export function PersonDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [person, setPerson] = useState<PersonDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingField, setEditingField] = useState<EditField>(null)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  // Load person details — API returns flat object (not wrapped)
  const fetchPerson = useCallback(async () => {
    if (!id) return
    try {
      setIsLoading(true)
      const data = await authFetchJson<PersonDetail>(`/api/v2/people/${id}`)
      setPerson(data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load person')
    } finally {
      setIsLoading(false)
    }
  }, [id])

  useEffect(() => {
    fetchPerson()
  }, [fetchPerson])

  const handleStartEdit = (field: EditField, value: string) => {
    setEditingField(field)
    setEditValue(value || '')
  }

  const handleSaveEdit = async (field: EditField) => {
    if (!person || !field) return

    try {
      setIsSaving(true)
      setError(null)
      await authFetchJson(`/api/v2/people/${person.id}`, {
        method: 'PATCH',
        json: { [field]: editValue || null },
      })
      // Re-fetch full detail to get affiliations + projects
      await fetchPerson()
      setEditingField(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancelEdit = () => {
    setEditingField(null)
    setEditValue('')
    setError(null)
  }

  const handleDeletePerson = async () => {
    if (!person) return
    try {
      setIsSaving(true)
      await authFetchJson(`/api/v2/people/${person.id}`, { method: 'DELETE' })
      navigate('/people')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete person')
      setIsSaving(false)
    }
  }

  const isResearcher = person?.tags
    ? Array.isArray(person.tags) &&
      person.tags.some(
        (tag) => typeof tag === 'string' && tag.toLowerCase() === 'researcher'
      )
    : false

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-primary-red border-t-transparent rounded-full animate-spin" />
          <span className="text-body text-text-secondary">Loading...</span>
        </div>
      </div>
    )
  }

  if (!person) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-body text-red-400 mb-4">
            {error || 'Person not found'}
          </p>
          <button
            onClick={() => navigate('/people')}
            className="px-4 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Back to People
          </button>
        </div>
      </div>
    )
  }

  const canDelete = !person.projects || person.projects.length === 0

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/people')}
          className="text-primary-red hover:text-red-700 mb-4 text-sm font-medium"
        >
          &larr; Back to People
        </button>
        <div className="flex items-center gap-3">
          <h1 className="text-h1 text-text-primary">{person.name}</h1>
          {isResearcher && (
            <span className="inline-block px-2.5 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-xs text-emerald-400 font-medium">
              Researcher
            </span>
          )}
        </div>
        {person.title && (
          <p className="text-body text-text-secondary mt-1">{person.title}</p>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-400/10 border border-red-400/20 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Basic Information */}
      <div className="rounded-lg bg-bg-surface border border-border-subtle p-6 mb-6">
        <h2 className="text-h2 text-text-primary mb-4">Basic Information</h2>
        <div className="space-y-4">
          <EditableRow
            label="Name"
            value={person.name}
            field="name"
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />
          <EditableRow
            label="Email"
            value={person.email}
            field="email"
            inputType="email"
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />
          <EditableRow
            label="Phone"
            value={person.phone}
            field="phone"
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />
          <EditableRow
            label="Title"
            value={person.title}
            field="title"
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />
          <div>
            <label className="text-xs font-medium text-text-tertiary uppercase">
              LinkedIn
            </label>
            {editingField === 'linkedin_url' ? (
              <div className="flex gap-2 mt-1">
                <input
                  type="url"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                />
                <button
                  onClick={() => handleSaveEdit('linkedin_url')}
                  disabled={isSaving}
                  className="px-3 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={handleCancelEdit}
                  className="px-3 py-2 bg-bg-body border border-border-subtle rounded-lg hover:bg-border-subtle transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <p
                onClick={() =>
                  handleStartEdit('linkedin_url', person.linkedin_url || '')
                }
                className="mt-1 text-body text-text-secondary cursor-pointer hover:text-primary-red transition-colors"
              >
                {person.linkedin_url ? (
                  <a
                    href={person.linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-primary-red hover:underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    View on LinkedIn
                  </a>
                ) : (
                  '\u2014'
                )}
              </p>
            )}
          </div>
          <EditableRow
            label="Notes"
            value={person.notes}
            field="notes"
            multiline
            editingField={editingField}
            editValue={editValue}
            isSaving={isSaving}
            onStartEdit={handleStartEdit}
            onSaveEdit={handleSaveEdit}
            onCancelEdit={handleCancelEdit}
            onEditValueChange={setEditValue}
          />
        </div>
      </div>

      {/* Research Profile — only for researchers */}
      {isResearcher && (
        <div className="rounded-lg bg-bg-surface border border-border-subtle p-6 mb-6">
          <h2 className="text-h2 text-text-primary mb-4">Research Profile</h2>
          <div className="space-y-4">
            {/* ORCID */}
            <div>
              <label className="text-xs font-medium text-text-tertiary uppercase">
                ORCID
              </label>
              {editingField === 'orcid' ? (
                <div className="flex gap-2 mt-1">
                  <input
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    placeholder="0000-0000-0000-0000"
                    className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                  />
                  <button
                    onClick={() => handleSaveEdit('orcid')}
                    disabled={isSaving}
                    className="px-3 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-2 bg-bg-body border border-border-subtle rounded-lg hover:bg-border-subtle transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <p
                  onClick={() => handleStartEdit('orcid', person.orcid || '')}
                  className="mt-1 text-body text-text-secondary cursor-pointer hover:text-primary-red transition-colors"
                >
                  {person.orcid ? (
                    <a
                      href={`https://orcid.org/${person.orcid}`}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-red hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {person.orcid}
                    </a>
                  ) : (
                    '\u2014'
                  )}
                </p>
              )}
            </div>

            {/* Google Scholar */}
            <div>
              <label className="text-xs font-medium text-text-tertiary uppercase">
                Google Scholar
              </label>
              {editingField === 'scholar_url' ? (
                <div className="flex gap-2 mt-1">
                  <input
                    type="url"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
                  />
                  <button
                    onClick={() => handleSaveEdit('scholar_url')}
                    disabled={isSaving}
                    className="px-3 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-2 bg-bg-body border border-border-subtle rounded-lg hover:bg-border-subtle transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <p
                  onClick={() =>
                    handleStartEdit('scholar_url', person.scholar_url || '')
                  }
                  className="mt-1 text-body text-text-secondary cursor-pointer hover:text-primary-red transition-colors"
                >
                  {person.scholar_url ? (
                    <a
                      href={person.scholar_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary-red hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      View Scholar Profile
                    </a>
                  ) : (
                    '\u2014'
                  )}
                </p>
              )}
            </div>

            {/* h-index */}
            <div>
              <label className="text-xs font-medium text-text-tertiary uppercase">
                h-index
              </label>
              <p className="mt-1 text-body text-text-secondary">
                {person.h_index != null ? (
                  <span>
                    {person.h_index}
                    {person.h_index_source && (
                      <span className="text-text-tertiary text-sm ml-2">
                        (source: {person.h_index_source})
                      </span>
                    )}
                  </span>
                ) : (
                  '\u2014'
                )}
              </p>
            </div>

            {/* Area of Expertise */}
            <EditableRow
              label="Area of Expertise"
              value={person.area_of_expertise}
              field="area_of_expertise"
              editingField={editingField}
              editValue={editValue}
              isSaving={isSaving}
              onStartEdit={handleStartEdit}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              onEditValueChange={setEditValue}
            />
          </div>
        </div>
      )}

      {/* Affiliations */}
      {person.affiliations && person.affiliations.length > 0 && (
        <div className="rounded-lg bg-bg-surface border border-border-subtle p-6 mb-6">
          <h2 className="text-h2 text-text-primary mb-4">Affiliations</h2>
          <div className="space-y-2">
            {person.affiliations.map((aff) => (
              <Link
                key={aff.id}
                to={`/organizations/${aff.organization_id}`}
                className="flex justify-between items-start p-4 rounded-lg bg-bg-body border border-border-subtle hover:border-primary-red transition-colors block"
              >
                <div>
                  <p className="text-body font-medium text-text-primary">
                    {aff.org_name}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {aff.role || 'Member'}
                    {aff.title && ` \u2022 ${aff.title}`}
                  </p>
                  <p className="text-xs text-text-tertiary mt-1">
                    {aff.start_date || 'Unknown start'}
                    {' \u2013 '}
                    {aff.end_date || 'Current'}
                    {aff.is_primary ? ' \u2022 Primary' : ''}
                  </p>
                </div>
                <span className="text-primary-red text-sm">&rarr;</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Affiliations empty state */}
      {(!person.affiliations || person.affiliations.length === 0) && (
        <div className="rounded-lg bg-bg-surface border border-border-subtle p-6 mb-6">
          <h2 className="text-h2 text-text-primary mb-2">Affiliations</h2>
          <p className="text-sm text-text-tertiary">No affiliations recorded</p>
        </div>
      )}

      {/* Linked Projects */}
      {person.projects && person.projects.length > 0 && (
        <div className="rounded-lg bg-bg-surface border border-border-subtle p-6 mb-6">
          <h2 className="text-h2 text-text-primary mb-4">Linked Projects</h2>
          <div className="space-y-2">
            {person.projects.map((proj) => (
              <Link
                key={proj.id}
                to={`/projects/${proj.id}`}
                className="flex justify-between items-start p-4 rounded-lg bg-bg-body border border-border-subtle hover:border-primary-red transition-colors block"
              >
                <div>
                  <p className="text-body font-medium text-text-primary">
                    {proj.title}
                  </p>
                  <p className="text-sm text-text-secondary">
                    {proj.person_role}
                  </p>
                </div>
                <span className="text-primary-red text-sm">&rarr;</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Delete Section */}
      <div className="rounded-lg bg-red-400/5 border border-red-400/20 p-6">
        <h3 className="text-h3 text-red-400 mb-2">Delete Person</h3>
        <p className="text-sm text-text-secondary mb-4">
          {canDelete
            ? 'This person record will be permanently deleted.'
            : `Cannot delete: this person is linked to ${person.projects?.length || 0} project(s).`}
        </p>
        {canDelete && (
          <>
            {deleteConfirm ? (
              <div className="flex gap-2">
                <button
                  onClick={handleDeletePerson}
                  disabled={isSaving}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                >
                  Confirm Delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(false)}
                  className="px-4 py-2 bg-bg-surface border border-border-subtle rounded-lg hover:bg-border-subtle transition-colors"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                onClick={() => setDeleteConfirm(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Delete Person
              </button>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Reusable inline-edit row ───────────────────────────────────────────────

interface EditableRowProps {
  label: string
  value: string | null | undefined
  field: EditField
  inputType?: string
  multiline?: boolean
  editingField: EditField
  editValue: string
  isSaving: boolean
  onStartEdit: (field: EditField, value: string) => void
  onSaveEdit: (field: EditField) => void
  onCancelEdit: () => void
  onEditValueChange: (value: string) => void
}

function EditableRow({
  label,
  value,
  field,
  inputType = 'text',
  multiline,
  editingField,
  editValue,
  isSaving,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditValueChange,
}: EditableRowProps) {
  const isEditing = editingField === field

  return (
    <div>
      <label className="text-xs font-medium text-text-tertiary uppercase">
        {label}
      </label>
      {isEditing ? (
        <div className="flex gap-2 mt-1">
          {multiline ? (
            <textarea
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              rows={3}
              className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
            />
          ) : (
            <input
              type={inputType}
              value={editValue}
              onChange={(e) => onEditValueChange(e.target.value)}
              className="flex-1 px-3 py-2 bg-bg-body border border-border-subtle rounded-lg text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-red"
            />
          )}
          <div className={multiline ? 'flex flex-col gap-2' : 'flex gap-2'}>
            <button
              onClick={() => onSaveEdit(field)}
              disabled={isSaving}
              className="px-3 py-2 bg-primary-red text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors text-sm"
            >
              Save
            </button>
            <button
              onClick={onCancelEdit}
              className="px-3 py-2 bg-bg-body border border-border-subtle rounded-lg hover:bg-border-subtle transition-colors text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <p
          onClick={() => onStartEdit(field, value || '')}
          className={`mt-1 text-body cursor-pointer hover:text-primary-red transition-colors ${
            multiline ? 'whitespace-pre-wrap' : ''
          } ${value ? 'text-text-primary' : 'text-text-tertiary'}`}
        >
          {value || '\u2014'}
        </p>
      )}
    </div>
  )
}
